/**
 * Runner-Management — Roster, Attribute, Teams
 */
import { formatCash, state } from '../main';
import { fixture } from '../data/fixture';
import type { RunnerDTO, TeamDTO, ActionHistoryEntry, ManagerDTO, RecruitCandidate, BuildingDTO } from '../data/fixture';
import { t } from '../i18n';
import { assignManager, hireRunner, createTeam, deleteTeam, assignToTeam, unassignFromTeam, setTeamLeader } from '../api';
import { portraitHTML, recruitPortraitHTML, hydratePortraits } from '../scene/PortraitEngine';
import { heatColor, loyColor, hpColor, attrColor, resultColor } from '../utils/colors';
import { statusBadge, skillChips, miniBar, attrMiniBar, attrBar, stateBar } from '../utils/ui';
import { rescueRunner, bribeRelease, submitPlan } from '../api';
import { showRescueModal } from './rescue-runner-modal';
import {
  renderCyberwareUI, wireCyberwareUI, injectCyberwareStyles,
  type CyberwareUIOptions,
} from './cyberware-section';
import {
  renderLoadoutUI, wireLoadoutUI, injectLoadoutStyles,
  type LoadoutUIOptions,
} from './loadout-section';

// Inject the shared cyberware + loadout section styles at module load so the
// styles are present before any RunnerDetail renders.
injectCyberwareStyles();
injectLoadoutStyles();

type Runner = RunnerDTO;

function renderActionHistory(history: ActionHistoryEntry[]): string {
  if (history.length === 0) {
    return `<div class="detail-empty">${t('runners.history_empty')}</div>`;
  }
  return history.slice(0, 5).map(h => `<div class="history-entry">
    <span class="history-day">${t('runners.history_day', { day: String(h.day) })}</span>
    <span class="history-action">${h.action}</span>
    <span class="history-target">${h.target}</span>
    <span class="history-result result-${h.result.toLowerCase()}" style="color:${resultColor(h.result)}">${h.result}</span>
  </div>`).join('');
}


function renderDetailPanel(r: Runner): string {
  const attrs = [
    { label: 'STR', val: r.str },
    { label: 'INT', val: r.int },
    { label: 'STL', val: r.stl },
    { label: 'TCH', val: r.tch },
    { label: 'CHA', val: r.cha },
    { label: 'CON', val: r.con },
  ];
  const avg = Math.round(attrs.reduce((s, a) => s + a.val, 0) / attrs.length);

  return `
    <div class="detail-header">
      <span class="detail-name">${r.name}</span>
      <span style="font-size:12px">${skillChips(r.skills)}</span>
      ${statusBadge(r)}
      <button class="btn-icon" style="margin-left:auto" id="detail-close">✕</button>
    </div>
    <div style="text-align:center;margin:12px 0">${portraitHTML(r,280)}</div>
    <div class="panel" id="runner-detail-attrs" style="margin-bottom:12px">
      <div class="panel-title">${t('runners.detail_attributes')} <span style="font-size:12px;color:var(--text-lo);margin-left:8px">∅ ${avg}</span></div>
      ${attrs.map(a => attrBar(a.label, a.val)).join('')}
    </div>
    <div class="panel" id="runner-detail-states" style="margin-bottom:12px">
      <div class="panel-title">${t('runners.detail_status')}</div>
      ${stateBar('HP', r.hp, hpColor(r.hp), r.hp < 30)}
      ${stateBar('LOY', r.loy, loyColor(r.loy), r.loy < 40)}
      ${stateBar('HEAT', r.heat, heatColor(r.heat), r.heat > 60)}
      ${stateBar('STRS', r.stress, r.stress > 70 ? 'var(--red)' : 'var(--text-lo)', r.stress > 70)}
    </div>
    ${renderLoadoutUI(buildLoadoutOpts(r))}
    ${(r.actionHistory ?? []).length > 0 ? `<div class="panel" id="runner-detail-history" style="margin-bottom:12px">
      <div class="panel-title">${t('runners.detail_history')}</div>
      ${renderActionHistory(r.actionHistory ?? [])}
    </div>` : ''}
    ${renderArrestBlock(r)}
    <div id="runner-detail-cyberware">${renderCyberwareUI(buildCyberwareOpts(r))}</div>
  `;
}

/** Build the shared-module options for the active-roster detail panel.
 *  Differs from the playback path: an install callback IS supplied, which
 *  flips the section from display-only into a fully wired INSTALL_CYBERWARE
 *  submission flow. Closes #615 follow-up. */
function buildCyberwareOpts(r: Runner): CyberwareUIOptions {
  return {
    runner: r,
    ripperdocs: fixture.player.ripperdocs,
    cleanCash: fixture.player.cleanCash,
    onInstallCyberware: async (runnerId, ripperdocNodeId, cyberwareId, targetPart) => {
      const sessionId = state.sessionId;
      if (!sessionId) return false;
      // submitPlan replaces the whole plan for the runner — so we pull
      // the runner's existing plan entries (if any) and append the
      // install action rather than wiping previously-queued actions.
      // Other call sites do the same coalescing (see economy.ts).
      const resp = await submitPlan(sessionId, {
        hood_id: runnerId,
        actions: [{
          runner: runnerId,
          command: 'install_cyberware',
          target: '',
          buildings: [ripperdocNodeId],
          cyberwareId,
          targetPart,
        }],
      });
      return resp.ok;
    },
    onRemoveCyberware: async (runnerId, ripperdocNodeId, targetPart) => {
      // Phase 4b — REMOVE_CYBERWARE. Doesn't carry cyberwareId (the
      // slot's existing CatalogIdx is the source of truth server-side).
      const sessionId = state.sessionId;
      if (!sessionId) return false;
      const resp = await submitPlan(sessionId, {
        hood_id: runnerId,
        actions: [{
          runner: runnerId,
          command: 'remove_cyberware',
          target: '',
          buildings: [ripperdocNodeId],
          targetPart,
        }],
      });
      return resp.ok;
    },
  };
}

/** Build the owner-only loadout options: stores from fixture.player, plus
 *  buy/equip/unequip/manufacture callbacks that emit single-action plans via
 *  submitPlan. The UI only emits; the server validates tier / affordability /
 *  possession. Mirrors buildCyberwareOpts. */
function buildLoadoutOpts(r: Runner): LoadoutUIOptions {
  const submitItem = async (runnerId: string, command: string, targetRef: string): Promise<boolean> => {
    const sessionId = state.sessionId;
    if (!sessionId) return false;
    const resp = await submitPlan(sessionId, {
      hood_id: runnerId,
      actions: [{ runner: runnerId, command, target: '', targetRef }],
    });
    return resp.ok;
  };
  return {
    runner: r,
    armory: fixture.player.armory,
    consumables: fixture.player.consumables,
    armoryCapacity: fixture.player.armoryCapacity,
    consumableCapacity: fixture.player.consumableCapacity,
    dirtyCash: fixture.player.dirtyCash,
    onBuy: (id, itemType) => submitItem(id, 'buy_weapon', `item:${itemType}`),
    onEquip: (id, itemType) => submitItem(id, 'equip', `item:${itemType}`),
    onUnequip: (id, slot) => submitItem(id, 'unequip', `slot:${slot}`),
    onManufacture: (id, itemType) => submitItem(id, 'manufacture', `item:${itemType}`),
  };
}

/** M4b-redux: detail-panel block for arrested runners. Shows severity badge,
 *  sentence breakdown (Base + recidivism − Judge − Mayor = Final), and
 *  Rescue-CTA button that opens the rescue-modal. */
function renderArrestBlock(r: Runner): string {
  if (r.status !== 'arrested' || !r.arrestSeverity) {
    return '';
  }
  const bd = r.sentenceBreakdown;
  const severityColor = r.arrestSeverity === 'federal' ? 'var(--red,#e63946)'
    : r.arrestSeverity === 'serious' ? 'var(--orange,#f77)'
    : r.arrestSeverity === 'moderate' ? 'var(--amber,#fa3)'
    : 'var(--text-mid,#bbb)';
  const finalDisplay = bd && bd.finalDays === -1
    ? t('runners.arrest_indefinite')
    : t('runners.arrest_days_remaining', { days: String(bd?.finalDays ?? '?') });
  let breakdownHTML = '';
  if (bd) {
    const parts: string[] = [`${t('runners.arrest_base')} ${bd.baseDays}d`];
    if (bd.recidivismAdd) parts.push(`+${bd.recidivismAdd}d ${t('runners.arrest_recidivism')}`);
    if (bd.judgeReduction) parts.push(`−${bd.judgeReduction}d ${t('runners.arrest_judge')}`);
    if (bd.mayorReduction) parts.push(`−${bd.mayorReduction}d ${t('runners.arrest_mayor')}`);
    const finalPart = bd.finalDays === -1 ? t('runners.arrest_indefinite') : `${bd.finalDays}d`;
    breakdownHTML = `<div class="arrest-breakdown" style="font-size:11px;color:var(--text-lo,#888);margin-top:6px;font-family:var(--mono,monospace)">${parts.join(' ')} = <strong style="color:var(--text-hi,#eef)">${finalPart}</strong></div>`;
  }
  return `<div class="panel" id="runner-detail-arrest" style="margin-bottom:12px;border-left:3px solid ${severityColor}">
    <div class="panel-title" style="color:${severityColor};text-transform:uppercase;letter-spacing:1px">⚠ ${t('runners.arrest_title')}</div>
    <div style="font-size:12px;margin-bottom:4px">
      ${t('runners.arrest_severity_label')}: <strong style="color:${severityColor}">${r.arrestSeverity.toUpperCase()}</strong>
      ${r.arrestedForAction ? ` · ${t('runners.arrest_for')} <code>${r.arrestedForAction}</code>` : ''}
    </div>
    <div style="font-size:13px;color:var(--text-hi,#eef)">${finalDisplay}</div>
    ${breakdownHTML}
    <button class="rescue-cta" data-rescue-id="${r.id}" style="margin-top:10px;background:var(--red,#e63946);border:0;color:#fff;padding:6px 14px;font-family:var(--mono,monospace);font-size:12px;letter-spacing:1px;cursor:pointer;border-radius:3px">
      ${t('runners.arrest_cta_rescue')}
    </button>
  </div>`;
}


export function renderRunners(): HTMLElement {
  const runners: Runner[] = fixture.player.runners;

  const root = document.createElement('div');
  root.className = 'screen';

  const style = document.createElement('style');
  style.textContent = `
    .runner-table { width:100%; border-collapse:collapse; font-size:13px; }
    .runner-table th { text-align:left; padding:7px 10px; font-family:var(--display); font-size:9px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-lo); border-bottom:1px solid var(--line); white-space:nowrap; }
    .runner-table td { padding:7px 10px; border-bottom:1px solid rgba(255,255,255,0.04); vertical-align:middle; }
    .runner-table tr:hover td { background: var(--bg-hover); }
    .attr-cell { font-family:var(--mono); font-size:12px; color:var(--text-mid); width:68px; white-space:nowrap; }
    .state-cell { white-space:nowrap; }
    /* .runners-tab → shared .tab · .btn-secondary → shared .btn (marker kept on filter buttons) */
    .runner-table th.sortable { cursor:pointer; user-select:none; transition:color .15s; }
    .runner-table th.sortable:hover { color:var(--cyan); }
    .runner-table th.sort-asc::after { content:' ▲'; font-size:9px; color:var(--cyan); }
    .runner-table th.sort-desc::after { content:' ▼'; font-size:9px; color:var(--cyan); }
    .runner-table th.sort-asc, .runner-table th.sort-desc { color:var(--cyan); }
    /* .btn-filter → shared .btn.sm (+ .active); .btn-filter kept as JS marker, no CSS */
    .runner-table tr { cursor:pointer; }
    .runner-table tr.selected td { background:rgba(var(--ac),0.06); }
    #runner-detail { display:none; min-width:300px; max-width:360px; flex-shrink:0; }
    #runner-detail.open { display:block; }
    .detail-header { display:flex; align-items:center; gap:8px; margin-bottom:12px; flex-wrap:wrap; }
    .detail-name { font-weight:700; font-size:16px; color:var(--text-hi); }
    /* .detail-close → shared .btn-icon (+ margin-left:auto inline) */
    .detail-attr-row { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
    .detail-attr-label { font-family:var(--mono); font-size:11px; color:var(--text-lo); width:28px; text-transform:uppercase; letter-spacing:1px; }
    .detail-attr-track { flex:1; height:6px; background:rgba(255,255,255,0.08); border-radius:2px; overflow:hidden; }
    .detail-attr-fill { height:100%; border-radius:3px; transition:width .2s; }
    .detail-attr-val { font-family:var(--mono); font-size:12px; color:var(--text-mid); width:24px; text-align:right; }
    .detail-state-row { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
    .detail-state-label { font-family:var(--mono); font-size:11px; color:var(--text-lo); width:36px; text-transform:uppercase; letter-spacing:1px; }
    .detail-state-val { font-family:var(--mono); font-size:12px; width:24px; text-align:right; }
    .detail-state-warn { color:var(--red); font-size:12px; }
    .detail-empty { color:var(--text-lo); font-size:12px; font-style:italic; padding:4px 0; }
    .history-entry { display:flex; align-items:center; gap:8px; margin-bottom:4px; font-size:12px; }
    .history-day { font-family:var(--mono); font-size:12px; color:var(--text-lo); width:48px; }
    .history-action { color:var(--text-mid); flex:1; }
    .history-target { color:var(--text-lo); font-family:var(--mono); font-size:11px; }
    .history-result { font-family:var(--mono); font-size:12px; font-weight:600; width:56px; text-align:right; }
    .training-slot { display:flex; align-items:center; gap:8px; margin-bottom:6px; font-size:12px; }
    .training-slot-empty { color:var(--cyan); cursor:pointer; font-size:12px; }
    .training-slot-empty:hover { text-decoration:underline; }
    .runner-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; background:var(--bg-deep); border:1px solid var(--border); border-radius:6px; font-size:13px; color:var(--text-mid); cursor:grab; user-select:none; transition:all .2s ease; margin:4px; }
    .runner-chip:active { cursor:grabbing; transform:scale(0.97); }
    .runner-chip:hover { border-color:var(--cyan-dim); background:rgba(var(--ac),0.04); box-shadow:0 0 8px rgba(var(--ac),0.08); }
    .runner-chip .chip-role { font-size:10px; color:var(--text-lo); font-family:var(--mono); }
    .team-card { border:1px solid var(--line); border-radius:8px; background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); margin-bottom:16px; overflow:hidden; transition:border-color .2s; }
    .team-card:hover { border-color:rgba(var(--ac),0.2); }
    .team-card-header { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; background:linear-gradient(135deg, rgba(var(--ac),0.03) 0%, rgba(0,0,0,0) 100%); border-bottom:1px solid var(--border); }
    .team-card-header .team-name { font-weight:700; font-size:15px; color:var(--text-hi); letter-spacing:0.3px; }
    .team-card-meta { display:flex; gap:12px; align-items:center; margin-top:4px; }
    .team-card-badge { font-family:var(--mono); font-size:11px; padding:2px 8px; border-radius:4px; }
    .team-drop-zone { min-height:48px; padding:8px 12px; border:2px dashed transparent; border-radius:0 0 6px 6px; transition:border-color .2s,background .2s; }
    .team-drop-zone.drag-over { border-color:var(--cyan); background:rgba(var(--ac),0.04); }
    .drop-hint { display:none; text-align:center; color:var(--cyan-dim); font-size:12px; padding:10px 0; font-family:var(--mono); letter-spacing:0.5px; }
    .team-drop-zone.drag-over .drop-hint { display:block; }
    .team-roster-table { width:100%; border-collapse:collapse; font-size:12px; }
    .team-roster-table th { text-align:left; padding:4px 6px; font-family:var(--mono); font-size:10px; letter-spacing:0.5px; text-transform:uppercase; color:var(--text-lo); border-bottom:1px solid var(--border); white-space:nowrap; }
    .team-roster-table td { padding:5px 6px; border-bottom:1px solid rgba(255,255,255,0.04); vertical-align:middle; }
    tr.team-member-row { cursor:grab; user-select:none; transition:background .15s; }
    tr.team-member-row:hover td { background:rgba(255,255,255,0.03); }
    .team-member-star { background:none; border:none; font-size:15px; cursor:pointer; color:var(--text-lo); opacity:0.3; padding:0 4px; transition:all .2s; }
    .team-member-star:hover { opacity:0.8; transform:scale(1.15); }
    .team-member-star.active { color:#f5c542; opacity:1; text-shadow:0 0 6px rgba(245,197,66,0.4); }
    .team-member-remove { background:none; border:none; color:var(--text-lo); cursor:pointer; font-size:13px; padding:2px 6px; margin-left:auto; transition:all .15s; border-radius:3px; }
    .team-member-remove:hover { color:var(--red); background:rgba(239,68,68,0.08); }
    .team-counter { font-family:var(--mono); font-size:11px; color:var(--text-lo); padding:2px 8px; border-radius:4px; background:rgba(255,255,255,0.04); border:1px solid var(--border); }
    .error-flash { animation:errorFlash 0.6s ease; }
    @keyframes errorFlash { 0%,100% { border-color:var(--border); } 50% { border-color:var(--red); box-shadow:0 0 12px rgba(239,68,68,0.3); } }
    .unassigned-drop-zone { min-height:40px; padding:8px 12px; border:2px dashed transparent; border-radius:6px; transition:border-color .2s,background .2s; display:flex; flex-wrap:wrap; gap:4px; }
    .unassigned-drop-zone.drag-over { border-color:var(--amber); background:rgba(245,197,66,0.04); }
    .manager-card { border:1px solid var(--line); border-radius:8px; background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); margin-bottom:16px; overflow:hidden; transition:border-color .2s; }
    .manager-card:hover { border-color:rgba(var(--ac),0.2); }
    .manager-card-header { display:flex; align-items:center; gap:14px; padding:14px 16px; background:linear-gradient(135deg, rgba(var(--ac),0.04) 0%, rgba(0,0,0,0) 100%); border-bottom:1px solid var(--border); }
    .manager-card-body { padding:8px 16px 12px; }
    .manager-assigned-row { display:flex; align-items:center; gap:10px; padding:6px 8px; border-bottom:1px solid rgba(255,255,255,0.04); font-size:13px; transition:background .15s; border-radius:4px; }
    .manager-assigned-row:hover { background:rgba(255,255,255,0.03); }
    .manager-assigned-row:last-child { border-bottom:none; }
    .capacity-bar { height:6px; background:var(--border); border-radius:3px; overflow:hidden; flex:1; max-width:120px; }
    .capacity-bar-fill { height:100%; border-radius:3px; transition:width .3s ease; }
    /* .stat-pill → shared (identical); only the -dot/-val/-lbl children stay local */
    .stat-pill-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
    .stat-pill-val { font-size:18px; font-weight:700; color:var(--text-hi); line-height:1; }
    .stat-pill-lbl { font-size:11px; color:var(--text-mid); text-transform:uppercase; letter-spacing:0.5px; }
    .manager-picker { border-top:1px solid var(--border); margin-top:8px; padding-top:8px; }
    .manager-pick-row { display:flex; align-items:center; gap:8px; padding:6px 8px; border-radius:4px; cursor:pointer; transition:background .15s; }
    .manager-pick-row:hover { background:rgba(var(--ac),0.06); }
    .mgr-badge { font-family:var(--mono); font-size:10px; padding:2px 8px; border-radius:4px; background:rgba(var(--ac),0.08); border:1px solid rgba(var(--ac),0.25); color:var(--cyan); text-transform:uppercase; letter-spacing:0.5px; }
    .mgr-attr-row { display:flex; gap:6px; flex-wrap:wrap; }
    .mgr-attr { font-family:var(--mono); font-size:11px; padding:1px 6px; border-radius:3px; background:rgba(255,255,255,0.03); }
    .mgr-attr b { font-weight:700; }
    .mgr-building-section { padding:12px 16px; }
    .mgr-section-title { font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:1px; color:var(--text-lo); margin-bottom:8px; }
    .mgr-building-card { background:var(--bg-deep); border-radius:6px; padding:12px 14px; }
    .mgr-building-stats { display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; }
    .mgr-bstat { display:flex; flex-direction:column; gap:2px; }
    .mgr-bstat-lbl { font-family:var(--mono); font-size:9px; text-transform:uppercase; letter-spacing:0.5px; color:var(--text-lo); }
    .mgr-bstat-val { font-family:var(--mono); font-size:13px; font-weight:600; }
    .mgr-zone-badge { font-family:var(--mono); font-size:9px; padding:1px 5px; border-radius:3px; text-transform:uppercase; letter-spacing:0.5px; }
    .mgr-zone-inner { background:rgba(239,68,68,0.1); border:1px solid rgba(239,68,68,0.3); color:var(--red); }
    .mgr-zone-mid { background:rgba(245,197,66,0.1); border:1px solid rgba(245,197,66,0.3); color:var(--amber); }
    .mgr-zone-outer { background:rgba(34,197,94,0.1); border:1px solid rgba(34,197,94,0.3); color:var(--green); }
    .mgr-front-badge { font-family:var(--mono); font-size:9px; padding:1px 5px; border-radius:3px; background:rgba(245,197,66,0.12); border:1px solid rgba(245,197,66,0.3); color:var(--amber); text-transform:uppercase; letter-spacing:0.5px; }
    @media (max-width:1024px) {
      #runner-detail, #runner-compare { min-width:240px; max-width:min(360px,45vw); }
    }
  `;
  root.appendChild(style);

  const stats = {
    active:   runners.filter(r => r.status === 'active').length,
    arrested: runners.filter(r => r.status === 'arrested').length,
    injured:  runners.filter(r => r.status === 'injured').length,
    manager:  runners.filter(r => r.status === 'manager').length,
  };

  root.innerHTML += `
    <div class="screen-title">${t('runners.title', { count: String(runners.length) })}</div>

    <div style="display:flex; gap:12px; margin-bottom:16px; align-items:center">
      <div style="display:flex; gap:8px; flex-wrap:wrap">
        <div class="stat-pill"><span class="stat-pill-dot" style="background:var(--green)"></span><span class="stat-pill-val">${stats.active}</span><span class="stat-pill-lbl">${t('runners.stat_active')}</span></div>
        <div class="stat-pill"><span class="stat-pill-dot" style="background:var(--red)"></span><span class="stat-pill-val">${stats.arrested}</span><span class="stat-pill-lbl">${t('runners.stat_arrested')}</span></div>
        <div class="stat-pill"><span class="stat-pill-dot" style="background:var(--amber)"></span><span class="stat-pill-val">${stats.injured}</span><span class="stat-pill-lbl">${t('runners.stat_injured')}</span></div>
        <div class="stat-pill"><span class="stat-pill-dot" style="background:var(--cyan)"></span><span class="stat-pill-val">${stats.manager}</span><span class="stat-pill-lbl">${t('runners.stat_manager')}</span></div>
      </div>
      <div style="flex:1"></div>
      <button class="btn" id="btn-recruit-top" style="padding:8px 16px; color:var(--cyan); font-size:13px">${t('runners.btn_recruit')}</button>
    </div>

    <div style="display:flex; gap:0; border-bottom:1px solid var(--border); margin-bottom:16px">
      <button class="runners-tab tab active" id="runners-tab-roster">${t('runners.tab_roster')}</button>
      <button class="runners-tab tab" id="runners-tab-rekrutierung">${t('runners.tab_rekrutierung')}</button>
      <button class="runners-tab tab" id="runners-tab-manager">${t('runners.tab_manager')}</button>
    </div>

    <!-- Roster -->
    <div id="runners-view-roster">
      <div style="display:flex;gap:16px">
      <div style="flex:1;min-width:0">
      <div class="panel">
        <div class="panel-title">${t('runners.panel_roster')}</div>
        <div style="overflow-x:auto">
          <table class="runner-table">
            <thead>
              <tr>
                <th style="width:56px"><span class="sr-only">Portrait</span></th>
                <th class="sortable" data-sort="name">${t('runners.col_name')}</th>
                <th>${t('runners.col_skills')}</th>
                <th class="sortable" data-sort="status">${t('runners.col_status')}</th>
                <th class="sortable" data-sort="team">${t('runners.col_team')}</th>
                <th class="sortable" data-sort="str" title="Strength">STR</th>
                <th class="sortable" data-sort="int" title="Intelligence">INT</th>
                <th class="sortable" data-sort="stl" title="Stealth">STL</th>
                <th class="sortable" data-sort="tch" title="Tech">TCH</th>
                <th class="sortable" data-sort="cha" title="Charisma">CHA</th>
                <th class="sortable" data-sort="con" title="Constitution">CON</th>
                <th class="sortable" data-sort="hp">${t('runners.col_hp')}</th>
                <th class="sortable" data-sort="heat">${t('runners.col_heat')}</th>
                <th class="sortable" data-sort="loy">${t('runners.col_loy')}</th>
                <th class="sortable" data-sort="stress">${t('runners.col_stress')}</th>
                <th class="sortable" data-sort="salary">${t('runners.col_salary')}</th>
              </tr>
            </thead>
            <tbody id="runner-tbody"></tbody>
          </table>
        </div>
      </div>
      <div style="display:flex; gap:8px; margin-top:12px; align-items:center; flex-wrap:wrap">
        <button class="btn" id="btn-rekrutierung">${t('runners.btn_open_rekrutierung')}</button>
        <button class="btn" id="btn-manager">${t('runners.btn_manager')}</button>
        <span style="margin-left:auto;display:flex;gap:4px;align-items:center">
          <span style="font-family:var(--mono);font-size:11px;color:var(--text-lo);margin-right:4px">FILTER:</span>
          <button class="btn sm btn-filter active" data-filter="all">${t('runners.filter_all')}</button>
          <button class="btn sm btn-filter" data-filter="active">${t('runners.stat_active')}</button>
          <button class="btn sm btn-filter" data-filter="arrested">${t('runners.stat_arrested')}</button>
          <button class="btn sm btn-filter" data-filter="injured">${t('runners.stat_injured')}</button>
          <button class="btn sm btn-filter" data-filter="manager">${t('runners.stat_manager')}</button>
        </span>
      </div>
      </div>
      <div id="runner-detail" class="panel"></div>
      <div id="runner-compare" class="panel" style="display:none;min-width:300px;max-width:360px;flex-shrink:0"></div>
      </div>
      <div style="display:flex;align-items:center;gap:12px;margin-top:24px;margin-bottom:12px;border-top:1px solid var(--border);padding-top:16px">
        <span style="font-family:var(--mono);font-size:13px;color:var(--cyan);letter-spacing:1px">${t('runners.tab_teams')}</span>
        <span style="flex:1"></span>
        <input id="new-team-input" type="text" maxlength="24" placeholder="${t('runners.new_team_placeholder')}" style="width:200px;padding:6px 12px;background:var(--bg-deep);border:1px solid var(--border);color:var(--text-hi);font-family:var(--sans);font-size:13px;border-radius:4px;outline:none;transition:border-color .15s" />
        <button class="btn" id="btn-new-team">${t('runners.btn_new_team')}</button>
      </div>
      <div id="teams-container"></div>
    </div>

    <!-- Rekrutierung -->
    <div id="runners-view-rekrutierung" style="display:none">
      <div id="recruit-container"></div>
    </div>

    <!-- Manager -->
    <div id="runners-view-manager" style="display:none">
      <div id="manager-container"></div>
    </div>
  `;

  // Populate roster table
  const tbody = root.querySelector('#runner-tbody') as HTMLElement;
  const detailPanel = root.querySelector('#runner-detail') as HTMLElement;

  const comparePanel = root.querySelector('#runner-compare') as HTMLElement;
  let selectedRow: HTMLElement | null = null;

  // ── Team Colors ───────────────────────────────────────────────────────
  const TEAM_PALETTE = ['var(--cyan)', 'var(--amber)', '#e879f9', '#fb923c', '#34d399', '#f87171', '#818cf8', '#fbbf24'];
  const teamColorMap = new Map<string, string>();
  const teamNames = [...new Set(runners.filter(r => r.status !== 'manager').map(r => r.team).filter(Boolean))];
  teamNames.forEach((name, i) => { teamColorMap.set(name!, TEAM_PALETTE[i % TEAM_PALETTE.length]); });

  // ── Sort & Filter State ───────────────────────────────────────────────
  type SortKey = 'name' | 'status' | 'team' | 'str' | 'int' | 'stl' | 'tch' | 'cha' | 'con' | 'hp' | 'heat' | 'loy' | 'stress' | 'salary';
  let sortKey: SortKey | null = null;
  let sortDir: 'asc' | 'desc' = 'desc';
  let filterStatus: string = 'all';

  function getSortValue(r: Runner, key: SortKey): string | number {
    switch (key) {
      case 'name': return r.name.toLowerCase();
      case 'status': return r.status;
      case 'team': return r.team ?? '';
      default: return (r as unknown as Record<string, number>)[key] ?? 0;
    }
  }

  function sortedFilteredRunners(): Runner[] {
    let list = filterStatus === 'all' ? [...runners] : runners.filter(r => r.status === filterStatus);
    if (sortKey) {
      const key = sortKey;
      const dir = sortDir === 'asc' ? 1 : -1;
      list.sort((a, b) => {
        const va = getSortValue(a, key);
        const vb = getSortValue(b, key);
        if (typeof va === 'string') return va.localeCompare(vb as string) * dir;
        return ((va as number) - (vb as number)) * dir;
      });
    }
    return list;
  }

  function renderRosterRows() {
    tbody.innerHTML = '';
    const sorted = sortedFilteredRunners();
    for (const r of sorted) {
      const tr = document.createElement('tr');
      tr.dataset.runner = r.name;
      const isManager = r.status === 'manager';
      const teamColor = isManager ? 'var(--text-lo)' : (teamColorMap.get(r.team ?? '') ?? 'var(--text-lo)');
      const teamLabel = isManager ? '—' : (r.team ?? '—');
      tr.innerHTML = `
        <td style="width:56px;padding:4px">${portraitHTML(r,48)}</td>
        <td style="font-weight:600; color:var(--text-hi); white-space:nowrap">${r.name}</td>
        <td>${skillChips(r.skills)}</td>
        <td>${statusBadge(r)}</td>
        <td style="font-family:var(--mono); font-size:12px">${isManager ? '<span style="color:var(--text-lo)">—</span>' : `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${teamColor};margin-right:6px;vertical-align:middle"></span><span style="color:${teamColor}">${teamLabel}</span>`}</td>
        <td class="attr-cell">${attrMiniBar(r.str)}</td>
        <td class="attr-cell">${attrMiniBar(r.int)}</td>
        <td class="attr-cell">${attrMiniBar(r.stl)}</td>
        <td class="attr-cell">${attrMiniBar(r.tch)}</td>
        <td class="attr-cell">${attrMiniBar(r.cha)}</td>
        <td class="attr-cell">${attrMiniBar(r.con)}</td>
        <td class="state-cell">${miniBar(r.hp, hpColor(r.hp))} <span style="font-family:var(--mono);font-size:11px;color:${hpColor(r.hp)}">${r.hp}</span></td>
        <td class="state-cell">${miniBar(r.heat, heatColor(r.heat))} <span style="font-family:var(--mono);font-size:11px;color:${heatColor(r.heat)}">${r.heat}</span></td>
        <td class="state-cell">${miniBar(r.loy, loyColor(r.loy))} <span style="font-family:var(--mono);font-size:11px;color:${loyColor(r.loy)}">${r.loy}</span></td>
        <td class="state-cell">${miniBar(r.stress, r.stress > 70 ? 'var(--red)' : 'var(--text-lo)')} <span style="font-family:var(--mono);font-size:11px;color:var(--text-mid)">${r.stress}</span></td>
        <td style="font-family:var(--mono);font-size:12px;color:var(--text-mid)">${r.salary ? formatCash(r.salary)+t('runners.salary_per_day') : '—'}</td>
      `;
      tr.addEventListener('click', (e) => {
        if (e.shiftKey && detailPanel.classList.contains('open')) {
          openCompare(r, tr);
        } else {
          closeCompare();
          openDetail(r, tr);
        }
      });
      tbody.appendChild(tr);
    }
    hydratePortraits(tbody);
  }

  function openDetail(r: Runner, row: HTMLElement) {
    tbody.querySelectorAll('tr.selected').forEach(tr => tr.classList.remove('selected'));
    row.classList.add('selected');
    selectedRow = row;
    detailPanel.innerHTML = renderDetailPanel(r);
    detailPanel.classList.add('open');
    hydratePortraits(detailPanel);
    // Wire the shared cyberware section (selects + submit). Idempotent
    // if no install form rendered (e.g. arrested runner / no ripperdocs).
    wireCyberwareUI(detailPanel, buildCyberwareOpts(r));
    wireLoadoutUI(detailPanel, buildLoadoutOpts(r));
    detailPanel.querySelector('#detail-close')!.addEventListener('click', () => {
      detailPanel.classList.remove('open');
      detailPanel.innerHTML = '';
      row.classList.remove('selected');
      selectedRow = null;
      closeCompare();
    });
    // M4b-redux: wire up Rescue-CTA to open the rescue-modal.
    const rescueBtn = detailPanel.querySelector<HTMLButtonElement>('.rescue-cta');
    if (rescueBtn) {
      rescueBtn.addEventListener('click', () => {
        const pool = (fixture.player.runners ?? []).filter(x =>
          x.status === 'active' && x.id !== r.id
        );
        showRescueModal({
          arrestedRunner: r,
          rescuerPool: pool,
          onSubmit: async (mode) => {
            const sid = state.sessionId;
            if (mode === 'stealth') {
              const rescuerId = (document.getElementById('rescue-rescuer-select') as HTMLSelectElement | null)?.value;
              if (!rescuerId) return;
              await rescueRunner(sid, { arrestedRunnerId: r.id, rescuerRunnerId: rescuerId });
            } else {
              await bribeRelease(sid, { arrestedRunnerId: r.id });
            }
          },
        });
      });
    }
  }

  function openCompare(r: Runner, row: HTMLElement) {
    row.classList.add('selected');
    comparePanel.innerHTML = renderDetailPanel(r).replace(/id="detail-close"/g, 'id="compare-close"');
    comparePanel.style.display = 'block';
    hydratePortraits(comparePanel);
    comparePanel.querySelector('#compare-close')!.addEventListener('click', () => {
      closeCompare();
      row.classList.remove('selected');
    });
  }

  function closeCompare() {
    comparePanel.style.display = 'none';
    comparePanel.innerHTML = '';
    tbody.querySelectorAll('tr.selected').forEach(tr => {
      if (tr !== selectedRow) tr.classList.remove('selected');
    });
  }

  renderRosterRows();

  // Tab switching
  const tabs = [
    { btn: '#runners-tab-roster',        view: '#runners-view-roster' },
    { btn: '#runners-tab-rekrutierung',  view: '#runners-view-rekrutierung' },
    { btn: '#runners-tab-manager',       view: '#runners-view-manager' },
  ];
  function switchTab(idx: number) {
    tabs.forEach(({ btn: b, view: v }, i) => {
      (root.querySelector(b) as HTMLElement).classList.toggle('active', i === idx);
      (root.querySelector(v) as HTMLElement).style.display = i === idx ? '' : 'none';
    });
  }
  tabs.forEach(({ btn }, idx) => {
    root.querySelector(btn)!.addEventListener('click', () => switchTab(idx));
  });

  // Action buttons
  root.querySelector('#btn-recruit-top')!.addEventListener('click', () => switchTab(1));
  root.querySelector('#btn-rekrutierung')!.addEventListener('click', () => switchTab(1));
  root.querySelector('#btn-manager')!.addEventListener('click', () => switchTab(2));
  // Column header sort
  root.querySelectorAll('.runner-table th.sortable').forEach(th => {
    th.addEventListener('click', () => {
      const key = (th as HTMLElement).dataset.sort as SortKey;
      if (sortKey === key) {
        sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey = key;
        sortDir = (key === 'name' || key === 'status' || key === 'team') ? 'asc' : 'desc';
      }
      // Update header arrows
      root.querySelectorAll('.runner-table th.sortable').forEach(h => h.classList.remove('sort-asc', 'sort-desc'));
      th.classList.add(sortDir === 'asc' ? 'sort-asc' : 'sort-desc');
      renderRosterRows();
    });
  });

  // Status filter buttons
  root.querySelectorAll('.btn-filter').forEach(btn => {
    btn.addEventListener('click', () => {
      filterStatus = (btn as HTMLElement).dataset.filter ?? 'all';
      root.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      renderRosterRows();
    });
  });

  // Teams panel — use server data directly, log errors for missing runners
  const teamsData: TeamDTO[] = fixture.player.teams ?? [];
  if (!fixture.player.teams) console.error('[runners] fixture.player.teams is missing');
  const assignedNames = new Set<string>();
  for (const team of teamsData) for (const m of (team.members ?? [])) assignedNames.add(m);
  const unassignedRunners = runners.filter(r => r.status !== 'manager' && !assignedNames.has(r.name));
  const teamsContainer = root.querySelector('#teams-container') as HTMLElement;

  const teamCards = teamsData.map((team, teamIdx) => {
    const tIdx = team.idx ?? teamIdx;
    const tColor = TEAM_PALETTE[tIdx % TEAM_PALETTE.length];
    const delCap = team.delegationCap;
    const members = team.members ?? [];
    const leaderName = team.leader || '';
    const leaderRunner = leaderName ? runners.find(run => run.name === leaderName) : null;
    // Orphan leader is silently ignored — the team card renders without the
    // star marker. The server game state occasionally drifts (runner removed
    // or renamed without cleaning team.leader) and logging here used to
    // spam the console on every render. The UI is already defensive; no log.
    const memberTable = members.length === 0 ? '' : `
      <table class="team-roster-table">
        <thead><tr>
          <th style="width:24px"><span class="sr-only">Team-Leader</span></th>
          <th style="width:36px"><span class="sr-only">Portrait</span></th>
          <th>${t('runners.col_name')}</th>
          <th>${t('runners.col_skills')}</th>
          <th>STR</th><th>INT</th><th>STL</th><th>TCH</th><th>CHA</th><th>CON</th>
          <th>${t('runners.col_hp')}</th>
          <th>HT</th>
          <th>LOY</th>
          <th style="width:28px"><span class="sr-only">Aktion</span></th>
        </tr></thead>
        <tbody>
          ${members.map(mName => {
            const r = runners.find(run => run.name === mName);
            // Orphan members are silently skipped (server state drift — runner
            // was removed/renamed but team.members still lists the old name).
            if (!r) return '';
            const rIdx = r.idx ?? runners.indexOf(r);
            const isLeader = mName === leaderName;
            return `<tr class="team-member-row" draggable="true" data-runner-idx="${rIdx}" data-from-team-idx="${tIdx}">
              <td><button class="team-member-star${isLeader ? ' active' : ''}" data-team-idx="${tIdx}" data-runner-idx="${rIdx}">\u2605</button></td>
              <td>${portraitHTML(r,28)}</td>
              <td style="font-weight:${isLeader ? '700' : '500'};color:${isLeader ? 'var(--text-hi)' : 'var(--text-mid)'}">${r.name}</td>
              <td>${skillChips(r.skills)}</td>
              <td class="attr-cell">${attrMiniBar(r.str)}</td>
              <td class="attr-cell">${attrMiniBar(r.int)}</td>
              <td class="attr-cell">${attrMiniBar(r.stl)}</td>
              <td class="attr-cell">${attrMiniBar(r.tch)}</td>
              <td class="attr-cell">${attrMiniBar(r.cha)}</td>
              <td class="attr-cell">${attrMiniBar(r.con)}</td>
              <td><span style="font-family:var(--mono);font-size:11px;color:${hpColor(r.hp)}">${r.hp}</span></td>
              <td><span style="font-family:var(--mono);font-size:11px;color:${heatColor(r.heat)}">${r.heat}</span></td>
              <td><span style="font-family:var(--mono);font-size:11px;color:${loyColor(r.loy)}">${r.loy}</span></td>
              <td><button class="team-member-remove" data-team-idx="${tIdx}" data-runner-idx="${rIdx}">\u2715</button></td>
            </tr>`;
          }).join('')}
        </tbody>
      </table>`;

    const coordPct = Math.round(team.coordinationBonus * 100);
    return `
      <div class="team-card">
        <div class="team-card-header" style="border-left:3px solid ${tColor}">
          <div>
            <div style="display:flex;align-items:center;gap:10px">
              <span class="team-name" style="color:${tColor}">${team.name}</span>
              <span class="team-counter">${members.length} / 10</span>
              ${leaderRunner ? `<span style="font-size:11px;color:var(--text-lo)">Leader: <span style="color:var(--text-mid);font-weight:600">${leaderName}</span></span>` : ''}
            </div>
            <div class="team-card-meta">
              <span class="team-card-badge" style="background:${tColor}15;border:1px solid ${tColor}40;color:${tColor}">${t('runners.team_coordination', { pct: String(coordPct) })}</span>
              <span class="team-card-badge" style="background:rgba(255,255,255,0.03);border:1px solid var(--border);color:var(--text-lo)">${t('runners.team_delegation', { cap: String(delCap) })}</span>
            </div>
          </div>
          <button class="btn-delete-team btn sm red" data-team-idx="${tIdx}">${t('runners.btn_dissolve_team')}</button>
        </div>
        <div class="team-drop-zone" data-team-idx="${tIdx}">
          ${memberTable}
          ${members.length === 0 ? `<div style="color:var(--text-lo);font-size:13px;padding:12px 0;text-align:center">${t('runners.team_empty')}</div>` : ''}
          <div class="drop-hint">${t('runners.drop_hint')}</div>
        </div>
      </div>
    `;
  }).join('');

  const unassignedSection = unassignedRunners.length === 0 ? '' : `
    <div class="team-card" style="border-color:rgba(245,197,66,0.25)">
      <div class="team-card-header" style="border-left:3px solid var(--amber);background:linear-gradient(135deg, rgba(245,197,66,0.04) 0%, rgba(0,0,0,0) 100%)">
        <div>
          <span class="team-name" style="color:var(--amber)">${t('runners.unassigned_title')}</span>
          <span class="team-counter" style="margin-left:8px">${unassignedRunners.length}</span>
          <div style="color:var(--text-lo);font-size:11px;margin-top:4px">${t('runners.unassigned_warning')}</div>
        </div>
      </div>
      <div class="unassigned-drop-zone" id="unassigned-zone" style="padding:12px">
        ${unassignedRunners.map(r => {
          const rIdx = r.idx ?? runners.indexOf(r);
          return `<span class="runner-chip" draggable="true" data-runner-idx="${rIdx}">${r.name} <span class="chip-role">${skillChips(r.skills)}</span></span>`;
        }).join('')}
      </div>
    </div>
  `;

  teamsContainer.innerHTML = teamCards + unassignedSection;

  // Wire up delete team buttons
  teamsContainer.querySelectorAll('.btn-delete-team').forEach(btn => {
    btn.addEventListener('click', async () => {
      const idx = parseInt((btn as HTMLElement).dataset.teamIdx ?? '0');
      const sid = state.sessionId;
      if (!sid) return;
      try {
        await deleteTeam(sid, idx);
      } catch (e) {
        console.error('deleteTeam failed', e);
      }
    });
  });

  // --- DRAG START on chips and member rows ---
  const sid = state.sessionId;
  teamsContainer.querySelectorAll('.runner-chip, .team-member-row').forEach(el => {
    el.addEventListener('dragstart', (e: Event) => {
      const de = e as DragEvent;
      const runnerIdx = (el as HTMLElement).dataset.runnerIdx;
      const fromTeamIdx = (el as HTMLElement).dataset.fromTeamIdx;
      de.dataTransfer!.setData('application/json', JSON.stringify({ runnerIdx, fromTeamIdx }));
      de.dataTransfer!.effectAllowed = 'move';
      (el as HTMLElement).style.opacity = '0.4';
    });
    el.addEventListener('dragend', () => {
      (el as HTMLElement).style.opacity = '';
    });
  });

  // --- DROP ZONES (team zones) ---
  teamsContainer.querySelectorAll('.team-drop-zone').forEach(zone => {
    zone.addEventListener('dragenter', (e) => { e.preventDefault(); zone.classList.add('drag-over'); });
    zone.addEventListener('dragover', (e) => { e.preventDefault(); });
    zone.addEventListener('dragleave', (e) => {
      if (!zone.contains((e as DragEvent).relatedTarget as Node)) zone.classList.remove('drag-over');
    });
    zone.addEventListener('drop', async (e: Event) => {
      const de = e as DragEvent;
      de.preventDefault();
      zone.classList.remove('drag-over');
      const toTeamIdx = parseInt((zone as HTMLElement).dataset.teamIdx!);
      const data = JSON.parse(de.dataTransfer!.getData('application/json'));
      const runnerIdx = parseInt(data.runnerIdx);
      const fromTeamIdx = data.fromTeamIdx != null ? parseInt(data.fromTeamIdx) : null;

      // Check max 10
      if (zone.querySelectorAll('.team-member-row').length >= 10) {
        (zone.closest('.panel') as HTMLElement)?.classList.add('error-flash');
        setTimeout(() => (zone.closest('.panel') as HTMLElement)?.classList.remove('error-flash'), 600);
        return;
      }

      // Same team? ignore
      if (fromTeamIdx === toTeamIdx) return;

      if (!sid) return;
      try {
        if (fromTeamIdx != null) {
          await unassignFromTeam(sid, fromTeamIdx, runnerIdx);
        }
        await assignToTeam(sid, toTeamIdx, runnerIdx);
      } catch (err) {
        console.error('assign failed', err);
      }
    });
  });

  // --- UNASSIGNED DROP ZONE ---
  const unassignedZone = teamsContainer.querySelector('#unassigned-zone');
  if (unassignedZone) {
    unassignedZone.addEventListener('dragenter', (e) => { e.preventDefault(); unassignedZone.classList.add('drag-over'); });
    unassignedZone.addEventListener('dragover', (e) => { e.preventDefault(); });
    unassignedZone.addEventListener('dragleave', (e) => {
      if (!unassignedZone.contains((e as DragEvent).relatedTarget as Node)) unassignedZone.classList.remove('drag-over');
    });
    unassignedZone.addEventListener('drop', async (e: Event) => {
      const de = e as DragEvent;
      de.preventDefault();
      unassignedZone.classList.remove('drag-over');
      const data = JSON.parse(de.dataTransfer!.getData('application/json'));
      const fromTeamIdx = data.fromTeamIdx != null ? parseInt(data.fromTeamIdx) : null;
      if (fromTeamIdx == null) return;
      const runnerIdx = parseInt(data.runnerIdx);
      if (!sid) return;
      try {
        await unassignFromTeam(sid, fromTeamIdx, runnerIdx);
      } catch (err) {
        console.error('unassign failed', err);
      }
    });
  }

  // --- REMOVE (X) BUTTONS ---
  teamsContainer.querySelectorAll('.team-member-remove').forEach(btn => {
    btn.addEventListener('click', async () => {
      const teamIdx = parseInt((btn as HTMLElement).dataset.teamIdx!);
      const runnerIdx = parseInt((btn as HTMLElement).dataset.runnerIdx!);
      if (!sid) return;
      try {
        await unassignFromTeam(sid, teamIdx, runnerIdx);
      } catch (err) {
        console.error('unassign failed', err);
      }
    });
  });

  // --- STAR (LEADER) TOGGLES ---
  teamsContainer.querySelectorAll('.team-member-star').forEach(star => {
    star.addEventListener('click', async () => {
      const teamIdx = parseInt((star as HTMLElement).dataset.teamIdx!);
      const runnerIdx = parseInt((star as HTMLElement).dataset.runnerIdx!);
      if (!sid) return;
      const zone = (star as HTMLElement).closest('.team-drop-zone');
      zone?.querySelectorAll('.team-member-star').forEach(s => s.classList.remove('active'));
      (star as HTMLElement).classList.add('active');
      try {
        await setTeamLeader(sid, teamIdx, runnerIdx);
      } catch (err) {
        console.error('setLeader failed', err);
      }
    });
  });

  // Wire up "New Team" inline input + button
  const newTeamInput = root.querySelector('#new-team-input') as HTMLInputElement | null;
  const newTeamBtn = root.querySelector('#btn-new-team');

  const doCreateTeam = async () => {
    if (!newTeamInput) return;
    const name = newTeamInput.value.trim();
    if (!name) {
      newTeamInput.style.borderColor = 'var(--red)';
      newTeamInput.focus();
      return;
    }
    const sid = state.sessionId;
    if (!sid) return;
    newTeamInput.disabled = true;
    (newTeamBtn as HTMLButtonElement | null)?.setAttribute('disabled', '');
    try {
      await createTeam(sid, name);
      newTeamInput.value = '';
      newTeamInput.style.borderColor = '';
    } catch (e) {
      console.error('createTeam failed', e);
      newTeamInput.style.borderColor = 'var(--red)';
    } finally {
      newTeamInput.disabled = false;
      (newTeamBtn as HTMLButtonElement | null)?.removeAttribute('disabled');
      newTeamInput.focus();
    }
  };

  newTeamBtn?.addEventListener('click', doCreateTeam);
  newTeamInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') doCreateTeam();
  });
  newTeamInput?.addEventListener('focus', () => {
    if (newTeamInput) newTeamInput.style.borderColor = 'var(--cyan)';
  });
  newTeamInput?.addEventListener('blur', () => {
    if (newTeamInput) newTeamInput.style.borderColor = '';
  });

  // Manager panel
  const managers: ManagerDTO[] = fixture.player.managers ?? [];
  const buildings: BuildingDTO[] = fixture.player.buildings ?? [];
  const managerContainer = root.querySelector('#manager-container') as HTMLElement;
  if (managers.length === 0) {
    managerContainer.innerHTML = `
      <div class="panel" style="text-align:center;padding:32px">
        <div style="font-size:28px;margin-bottom:8px;opacity:0.3">&#x1F464;</div>
        <div class="panel-title" style="margin-bottom:4px">${t('runners.tab_manager')}</div>
        <div class="detail-empty">${t('runners.manager_empty')}</div>
      </div>`;
  } else {
    managerContainer.innerHTML = managers.map(m => {
      const mRunner = runners.find(run => run.name === m.name);
      const bldg = buildings.find(b => b.managerName === m.name);

      // Runner attributes for the manager
      const mAttrs = mRunner ? [
        { l: 'STR', v: mRunner.str }, { l: 'INT', v: mRunner.int }, { l: 'STL', v: mRunner.stl },
        { l: 'TCH', v: mRunner.tch }, { l: 'CHA', v: mRunner.cha }, { l: 'CON', v: mRunner.con },
      ] : [];
      const chaVal = mRunner?.cha ?? 0;

      // Building financials
      const bldgIncome = bldg?.income ?? 0;
      const bldgRent = bldg?.rent ?? 0;
      const bldgProfit = bldgIncome - bldgRent;
      const bldgHeat = bldg?.heat ?? 0;

      return `
        <div class="manager-card">
          <div class="manager-card-header">
            ${mRunner ? portraitHTML(mRunner, 64) : '<div style="width:64px;height:64px;border-radius:50%;background:var(--bg-deep)"></div>'}
            <div style="flex:1;min-width:0">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
                <span style="font-weight:700;font-size:16px;color:var(--text-hi)">${m.name}</span>
                <span class="mgr-badge">${t('runners.status_manager')}</span>
                ${m.specialization ? `<span style="font-size:11px;color:var(--text-lo)">${m.specialization}</span>` : ''}
              </div>
              <div class="mgr-attr-row">
                ${mAttrs.map(a => `<span class="mgr-attr" style="color:${attrColor(a.v)}">${a.l} <b>${a.v}</b></span>`).join('')}
              </div>
              <div style="display:flex;gap:12px;margin-top:4px;font-family:var(--mono);font-size:11px">
                <span style="color:${hpColor(mRunner?.hp ?? 0)}">HP ${mRunner?.hp ?? '—'}</span>
                <span style="color:${loyColor(mRunner?.loy ?? 0)}">LOY ${mRunner?.loy ?? '—'}</span>
                <span style="color:${heatColor(mRunner?.heat ?? 0)}">HEAT ${mRunner?.heat ?? '—'}</span>
              </div>
            </div>
          </div>

          ${bldg ? `
          <div class="mgr-building-section">
            <div class="mgr-section-title">${t('runners.manager_building')}</div>
            <div class="mgr-building-card" style="border-left:3px solid ${bldg.isFront ? 'var(--amber)' : 'var(--cyan)'}">
              <div style="display:flex;justify-content:space-between;align-items:start;margin-bottom:8px">
                <div>
                  <div style="font-weight:600;font-size:14px;color:var(--text-hi)">${bldg.name}</div>
                  <div style="display:flex;gap:8px;margin-top:3px;align-items:center">
                    <span style="font-size:11px;color:var(--text-lo)">${bldg.type}</span>
                    <span class="mgr-zone-badge mgr-zone-${bldg.zone ?? 'mid'}">${bldg.zone ?? 'mid'}</span>
                    <span style="font-family:var(--mono);font-size:10px;color:var(--text-lo)">${bldg.sector}</span>
                    ${bldg.isFront ? `<span class="mgr-front-badge">${t('runners.manager_front')}</span>` : ''}
                  </div>
                </div>
              </div>
              <div class="mgr-building-stats">
                <div class="mgr-bstat">
                  <span class="mgr-bstat-lbl">${t('runners.manager_income')}</span>
                  <span class="mgr-bstat-val" style="color:var(--green)">${formatCash(bldgIncome)}</span>
                </div>
                <div class="mgr-bstat">
                  <span class="mgr-bstat-lbl">${t('runners.manager_rent')}</span>
                  <span class="mgr-bstat-val" style="color:var(--red)">-${formatCash(bldgRent)}</span>
                </div>
                <div class="mgr-bstat">
                  <span class="mgr-bstat-lbl">${t('runners.manager_profit')}</span>
                  <span class="mgr-bstat-val" style="color:${bldgProfit >= 0 ? 'var(--green)' : 'var(--red)'}">${bldgProfit >= 0 ? '+' : ''}${formatCash(bldgProfit)}</span>
                </div>
                <div class="mgr-bstat">
                  <span class="mgr-bstat-lbl">HEAT</span>
                  <span class="mgr-bstat-val" style="color:${heatColor(bldgHeat)}">${bldgHeat}</span>
                </div>
                <div class="mgr-bstat">
                  <span class="mgr-bstat-lbl">${t('runners.manager_defense')}</span>
                  <span class="mgr-bstat-val" style="color:var(--text-mid)">${bldg.defense ?? 0}</span>
                </div>
                <div class="mgr-bstat">
                  <span class="mgr-bstat-lbl">${t('runners.manager_value')}</span>
                  <span class="mgr-bstat-val" style="color:var(--text-mid)">${formatCash(bldg.propertyValue ?? 0)}</span>
                </div>
              </div>
            </div>
          </div>` : `
          <div class="mgr-building-section">
            <div class="mgr-section-title">${t('runners.manager_building')}</div>
            <div style="color:var(--text-lo);font-size:12px;font-style:italic;text-align:center;padding:12px 0">${t('runners.manager_no_building')}</div>
          </div>`}

          <div class="mgr-building-section" style="border-top:1px solid var(--border)">
            <div class="mgr-section-title">${t('runners.manager_delegation')}</div>
            <div style="display:flex;align-items:center;gap:10px;margin-top:4px">
              <span style="font-family:var(--mono);font-size:12px;color:var(--text-lo)">CHA ${chaVal} → ${t('runners.manager_cap_slots', { cap: String(m.delegationCapacity) })}</span>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Wire up assign buttons — show inline runner picker (currently unused, kept for future)
    managerContainer.querySelectorAll('.manager-assign-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const btnEl = btn as HTMLButtonElement;
        const mName = btnEl.dataset.manager;
        if (!mName) return;

        const mCard = btnEl.closest('.manager-card');
        if (!mCard) return;

        // Already has picker? toggle off
        const existing = mCard.querySelector('.manager-picker');
        if (existing) { existing.remove(); return; }

        // Eligible runners: active, not a manager, not already assigned to this manager
        const mgr = managers.find(m => m.name === mName);
        const alreadyAssigned = new Set(mgr?.assignedRunners ?? []);
        const eligible = runners.filter(r => r.status === 'active' && !alreadyAssigned.has(r.name));

        if (eligible.length === 0) return;

        const picker = document.createElement('div');
        picker.className = 'manager-picker';
        picker.innerHTML = `
          <div style="font-family:var(--mono);font-size:10px;text-transform:uppercase;letter-spacing:1px;color:var(--text-lo);margin-bottom:6px;padding:0 4px">${t('runners.manager_pick_runner')}</div>
          ${eligible.map(r => {
            const rIdx = r.idx ?? runners.indexOf(r);
            return `<div class="manager-pick-row" data-runner-idx="${rIdx}" data-runner-id="${r.name}">
              ${portraitHTML(r, 24)}
              <span style="font-size:12px;color:var(--text-mid)">${r.name}</span>
              <span style="font-size:10px;margin-left:auto">${skillChips(r.skills)}</span>
            </div>`;
          }).join('')}
        `;

        mCard.querySelector('.manager-card-body')!.appendChild(picker);
        hydratePortraits(picker);

        picker.querySelectorAll('.manager-pick-row').forEach(row => {
          row.addEventListener('click', async () => {
            const rId = (row as HTMLElement).dataset.runnerId;
            if (!rId) return;
            const sid = state.sessionId;
            if (!sid) return;
            (row as HTMLElement).style.opacity = '0.5';
            try {
              await assignManager(sid, { runnerId: rId, role: 'OPERATIVE' });
              picker.remove();
            } catch (e: unknown) {
              console.error('[runners] Assign failed:', (e instanceof Error ? e.message : String(e)));
              (row as HTMLElement).style.opacity = '1';
            }
          });
        });
      });
    });
  }

  // Recruit pool
  const recruitPool: RecruitCandidate[] = fixture.player.recruitPool ?? [];
  const recruitContainer = root.querySelector('#recruit-container') as HTMLElement;
  if (recruitPool.length === 0) {
    recruitContainer.innerHTML = `<div class="panel"><div class="panel-title">${t('runners.recruit_pool')}</div><div class="detail-empty">${t('runners.recruit_empty')}</div></div>`;
  } else {
    recruitContainer.innerHTML = `
      <div class="panel">
        <div class="panel-title" style="margin-bottom:12px">${t('runners.recruit_pool')}</div>
        <table class="runner-table" style="margin-top: 8px;">
          <thead>
            <tr>
              <th style="width:56px"><span class="sr-only">Portrait</span></th>
              <th>${t('runners.col_name')}</th>
              <th>${t('runners.col_level')}</th>
              <th>${t('runners.col_skills')}</th>
              <th style="color:var(--text-lo)" title="Strength">STR</th>
              <th style="color:var(--text-lo)" title="Intelligence">INT</th>
              <th style="color:var(--text-lo)" title="Stealth">STL</th>
              <th style="color:var(--text-lo)" title="Tech">TCH</th>
              <th style="color:var(--text-lo)" title="Charisma">CHA</th>
              <th style="color:var(--text-lo)" title="Constitution">CON</th>
              <th>${t('runners.col_loy_tendency')}</th>
              <th>${t('runners.col_special')}</th>
              <th>${t('runners.col_action')}</th>
            </tr>
          </thead>
          <tbody>
            ${recruitPool.map(c => {
              const isLow = c.scoutLevel === 'LOW';
              const isMid = c.scoutLevel === 'MID';
              const badgeColor = isLow ? 'var(--text-lo)' : isMid ? 'var(--amber)' : 'var(--green)';
              return `
                <tr>
                  <td style="width:56px;padding:4px">${recruitPortraitHTML(c,48)}</td>
                  <td style="font-weight:600;color:var(--text-hi)">${c.name}</td>
                  <td><span class="recruit-scout-badge" style="font-family:var(--mono);font-size:11px;padding:2px 6px;border-radius:3px;background:${badgeColor}22;border:1px solid ${badgeColor}55;color:${badgeColor}">${c.scoutLevel}</span></td>
                  <td>${skillChips(c.skills ?? [])}</td>
                  <td class="attr-cell">${isLow ? '?' : c.str ?? '?'}</td>
                  <td class="attr-cell">${isLow ? '?' : c.int ?? '?'}</td>
                  <td class="attr-cell">${isLow ? '?' : c.stl ?? '?'}</td>
                  <td class="attr-cell">${isLow ? '?' : c.tch ?? '?'}</td>
                  <td class="attr-cell">${isLow ? '?' : c.cha ?? '?'}</td>
                  <td class="attr-cell">${isLow ? '?' : c.con ?? '?'}</td>
                  <td style="font-family:var(--mono);font-size:11px;color:var(--text-mid)">${c.scoutLevel === 'HIGH' ? (c.loyTendency ?? '—') : '???'}</td>
                  <td style="font-size:11px">${skillChips(c.skills ?? [])}</td>
                  <td>
                    <button class="recruit-hire-btn btn sm" data-idx="${c.idx}">
                      ${t('runners.recruit_btn', { cost: formatCash(c.hiringCost) })}
                    </button>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;

    // Wire up hire buttons
    recruitContainer.querySelectorAll('.recruit-hire-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const btnEl = btn as HTMLButtonElement;
        const idxStr = btnEl.dataset.idx;
        if (idxStr === undefined || btnEl.disabled) return;

        const oldText = btnEl.textContent;
        btnEl.textContent = '...';
        btnEl.disabled = true;
        try {
          const sid = state.sessionId;
          await hireRunner(sid, { candidateIdx: parseInt(idxStr, 10) });
          btnEl.textContent = t('runners.btn_state_hired');
        } catch (e: unknown) {
          btnEl.textContent = t('runners.btn_state_failed');
          console.error('[runners] Hire failed:', (e instanceof Error ? e.message : String(e)));
          setTimeout(() => {
            btnEl.textContent = oldText;
            btnEl.disabled = false;
          }, 2000);
        }
      });
    });
  }

  // Hydrate portrait canvases
  hydratePortraits(root);

  // Source indicator
  const src = document.createElement('div');
  src.style.cssText = 'font-size:12px;color:var(--text-lo);margin-top:8px;font-family:var(--mono)';
  src.textContent = `[fixture v${fixture.engineVersion} · tag ${fixture.day}/${fixture.cycleMax}]`;
  root.appendChild(src);

  // .dash-stat is the shared frosted-tile component (index.html) — no local override.
  return root;
}
